import { beginViewerNavigation } from "@/components/FileViewer/services/viewerNavigation";
import store from "../../../redux/store";
import { setFileViewerData } from "@/redux/fileViewerSlice";
import { recordDocumentOpened } from "@/components/FpPages/Documents/browser/api";
import { isDocumentsProductPage } from "@/utils/productRoutes";
import { phiDocumentReview } from "@/stytchPublicTokens";

const openFile = ({
  fileId,
  fileName,
  fileType,
  unreadableReason = null,
  interactionId,
  doNotOpenDrawer = false,
}: {
  fileId: string;
  fileName: string;
  fileType?: string;
  /** Why an unreadable file cannot be read (#11277), for the viewer. */
  unreadableReason?: "encrypted" | "no_signature" | null;
  interactionId?: string;
  doNotOpenDrawer?: boolean;
}) => {
  beginViewerNavigation();
  if (fileId === "close") {
    store.dispatch(setFileViewerData({ isOpen: false }));

    // Clean up docId from URL when closing file viewer. Carries
    // history.state forward (#11742): a bare `{}` wipes the phone overlay's
    // back-dismissal marker and react-router's idx bookkeeping.
    const currentUrl = new URL(window.location.href);
    if (currentUrl.searchParams.has("docId")) {
      currentUrl.searchParams.delete("docId");
      currentUrl.searchParams.delete("interactionId");
      window.history.replaceState(
        window.history.state ?? {},
        "",
        currentUrl.toString()
      );
    }
    return;
  }

  const fileViewerData = store.getState().fileViewerData;
  const currentlyOpenedDocId = fileViewerData?.fileId;

  // Skip only while this document is actually on screen — a second click on
  // the open document shouldn't reset its scroll or highlight state. Once it
  // has been closed the id lingers in the slice (Layout's `closeDocumentPanel`
  // handler closes its panes without dispatching), and treating that as
  // "already open" made the next click on that same document do nothing at
  // all (#9746).
  if (
    currentlyOpenedDocId === fileId &&
    fileViewerData.interactionId === (interactionId ?? null) &&
    fileViewerData?.isOpen
  ) {
    return;
  }

  // Documents Home suggests what the viewer opened (#6559): note the open.
  // Test ids never reach the route.
  if (!fileId.includes("test-")) recordDocumentOpened(fileId);

  // Update URL with docId for deep linking when on the documents product's
  // page (not the intake queue a PHI deployment shows there, #11422 R8)
  if (isDocumentsProductPage(window.location.pathname, phiDocumentReview())) {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set("docId", fileId);
    if (interactionId)
      currentUrl.searchParams.set("interactionId", interactionId);
    else currentUrl.searchParams.delete("interactionId");
    window.history.replaceState({}, "", currentUrl.toString());
  }

  store.dispatch(
    setFileViewerData({
      isOpen: true,
      fileId,
      fileName,
      fileType,
      unreadableReason,
      interactionId: interactionId ?? null,
      doNotOpenDrawer,
    })
  );
};

export default openFile;
